import type { ApiResponse } from '../types';

/**
 * Error thrown by {@link unwrap} when the backend returns `{ success: false }`.
 *
 * Mirrors the shape of the failure branch of {@link ApiResponse}, plus a
 * `status` field (set to `200` because the HTTP call itself succeeded — only
 * the business-logic check failed).
 */
export class ApiResponseError extends Error {
  readonly code?: string;
  readonly details?: Record<string, unknown>;

  constructor(error: { message: string; code?: string; details?: Record<string, unknown> }) {
    super(error.message);
    this.name = 'ApiResponseError';
    this.code = error.code;
    this.details = error.details;
  }
}

/**
 * Unwrap an {@link ApiResponse} — return `data` on success, throw on failure.
 *
 * Use this in services that want to expose a "throw on error" style API to
 * their callers (i.e. the service returns `T`, not `ApiResponse<T>`). Callers
 * then handle errors via `try`/`catch` instead of manually narrowing on
 * `success`.
 *
 * For services that want to pass through the full response (so callers can
 * branch on success themselves), return the `ApiResponse<T>` directly and skip
 * this helper.
 */
export function unwrap<T>(res: ApiResponse<T>, fallbackMessage = 'Request failed'): T {
  if (res.success) return res.data;
  throw new ApiResponseError(res.error ?? { message: fallbackMessage });
}

/**
 * Extract a human-readable error message from any value — typically either an
 * {@link ApiResponse} failure or a caught exception. Safe to call on unknown
 * inputs; returns `fallback` when no message can be derived.
 */
export function errorMessage(value: unknown, fallback = 'Request failed'): string {
  if (!value) return fallback;
  if (value instanceof Error) return value.message || fallback;
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    const apiErr = v.error as { message?: string } | undefined;
    if (apiErr?.message) return apiErr.message;
    if (typeof v.message === 'string' && v.message) return v.message;
    const axiosData = (v.response as { data?: unknown } | undefined)?.data;
    if (axiosData) return errorMessage(axiosData, fallback);
  }
  return fallback;
}
